/**
 * SchemaSetPickerDialog — Select a target schema set for mapping.
 *
 * Follows the RecipePickerDialog pattern: Dialog -> fetch -> list -> select.
 * Two entry points: starter suggestion card and MappingOutputNode link.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { Schema as SchemaIcon } from '@mui/icons-material';
import { listSchemas } from '../../services/schemaApi';
import type { Schema } from '../../services/schemaApi';

interface SchemaSetPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (schema: Schema) => void;
  /** Currently selected schema ID for highlighting. */
  currentSchemaId?: string | null;
}

const SchemaSetPickerDialog: React.FC<SchemaSetPickerDialogProps> = ({
  open,
  onClose,
  onSelect,
  currentSchemaId,
}) => {
  const [schemaSets, setSchemaSets] = useState<Schema[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSchemaSets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listSchemas({ status: 'active' });
      setSchemaSets(result);
    } catch (e: any) {
      setError(e.message || 'Failed to load schema sets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchSchemaSets();
    }
  }, [open, fetchSchemaSets]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <SchemaIcon color="primary" />
        <Typography variant="h6" sx={{ fontSize: 16 }}>
          Select Target Schema
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Empty state */}
        {!loading && !error && schemaSets.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <SchemaIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No schemas available
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Create one in Settings &rarr; Schemas
            </Typography>
          </Box>
        )}

        {/* Schema list */}
        {!loading &&
          schemaSets.map((schema) => {
            const isCurrent = schema.schema_id === currentSchemaId;
            const tableCount = schema.definition?.tables ? Object.keys(schema.definition.tables).length : 0;

            return (
              <Card
                key={schema.schema_id}
                variant="outlined"
                sx={{
                  mb: 1,
                  borderColor: isCurrent ? 'primary.main' : 'divider',
                  bgcolor: isCurrent ? alpha('#3b82f6', 0.02) : 'transparent',
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: 13 }}>
                      {schema.schema_name}
                    </Typography>
                    <Chip
                      label={`v${schema.version}`}
                      size="small"
                      sx={{ fontSize: 9, height: 18, bgcolor: alpha('#64748b', 0.1) }}
                    />
                    {tableCount > 0 && (
                      <Chip
                        label={`${tableCount} table${tableCount !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 9, height: 18 }}
                      />
                    )}
                    {isCurrent && (
                      <Chip
                        label="Current"
                        size="small"
                        color="primary"
                        sx={{ fontSize: 9, height: 18 }}
                      />
                    )}
                  </Stack>
                  {schema.metadata?.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        display: 'block',
                        fontSize: 11,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {schema.metadata.description}
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0, pb: 1, px: 2 }}>
                  <Button
                    size="small"
                    variant={isCurrent ? 'outlined' : 'contained'}
                    onClick={() => onSelect(schema)}
                    sx={{ fontSize: 11, textTransform: 'none', py: 0.25, px: 2 }}
                  >
                    {isCurrent ? 'Change' : 'Select'}
                  </Button>
                </CardActions>
              </Card>
            );
          })}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SchemaSetPickerDialog;
